import { isUserAdmin } from './adminUtils';

/**
 * Secure logger utility that only logs messages if the user is an admin
 * This prevents sensitive data leaks in production for non-admin users
 */
class SecureLogger {
  private static instance: SecureLogger;
  private isAdmin: boolean = false;
  private adminCheckPromise: Promise<boolean> | null = null;
  private initialized: boolean = false;

  private constructor() {
    // Private constructor to enforce singleton pattern
  }

  public static getInstance(): SecureLogger {
    if (!SecureLogger.instance) {
      SecureLogger.instance = new SecureLogger();
    }
    return SecureLogger.instance;
  }

  /**
   * Initialize the logger by checking admin status
   * This should be called when the user logs in
   */
  public async init(): Promise<void> {
    if (!this.adminCheckPromise) {
      this.adminCheckPromise = isUserAdmin();
      this.isAdmin = await this.adminCheckPromise;
      this.initialized = true;
      this.adminCheckPromise = null; // Clear the promise after resolution
    } else {
      // Wait for the existing check to complete
      this.isAdmin = await this.adminCheckPromise;
      this.initialized = true;
    }
  }

  /**
   * Reset the logger when user logs out
   */
  public reset(): void {
    this.isAdmin = false;
    this.initialized = false;
    this.adminCheckPromise = null;
  }

  /**
   * Log a message if the user is an admin
   * @param message The message to log
   * @param args Additional arguments to log
   */
  public log(message: string, ...args: any[]): void {
    if (this.isAdmin) {
      console.log(message, ...args);
    }
  }

  /**
   * Log an error if the user is an admin
   * @param message The error message to log
   * @param args Additional arguments to log
   */
  public error(message: string, ...args: any[]): void {
    if (this.isAdmin) {
      console.error(message, ...args);
    }
  }

  /**
   * Log a warning if the user is an admin
   * @param message The warning message to log
   * @param args Additional arguments to log
   */
  public warn(message: string, ...args: any[]): void {
    if (this.isAdmin) {
      console.warn(message, ...args);
    }
  }

  /**
   * Log info if the user is an admin
   * @param message The info message to log
   * @param args Additional arguments to log
   */
  public info(message: string, ...args: any[]): void {
    if (this.isAdmin) {
      console.info(message, ...args);
    }
  }

  /**
   * Log debug information if the user is an admin
   * @param message The debug message to log
   * @param args Additional arguments to log
   */
  public debug(message: string, ...args: any[]): void {
    if (this.isAdmin) {
      console.debug(message, ...args);
    }
  }

  /**
   * Check if the current user is an admin
   * @returns True if the user is an admin, false otherwise
   */
  public isUserAdmin(): boolean {
    return this.isAdmin;
  }

  /**
   * Check if the logger has been initialized
   * @returns True if initialized, false otherwise
   */
  public isInitialized(): boolean {
    return this.initialized;
  }
}

// Export a singleton instance
export const secureLogger = SecureLogger.getInstance();

// Export a function to initialize the logger
export const initLogger = async (): Promise<void> => {
  await secureLogger.init();
};

// Export a function to reset the logger
export const resetLogger = (): void => {
  secureLogger.reset();
};

/**
 * Direct replacement for console.log that only logs if user is admin
 * This makes it easier to replace existing console.log calls
 * @param message The message to log
 * @param args Additional arguments to log
 */
export const log = (message: string, ...args: any[]): void => {
  secureLogger.log(message, ...args);
};

/**
 * Direct replacement for console.error that only logs if user is admin
 * @param message The error message to log
 * @param args Additional arguments to log
 */
export const error = (message: string, ...args: any[]): void => {
  secureLogger.error(message, ...args);
};

/**
 * Direct replacement for console.warn that only logs if user is admin
 * @param message The warning message to log
 * @param args Additional arguments to log
 */
export const warn = (message: string, ...args: any[]): void => {
  secureLogger.warn(message, ...args);
};

/**
 * Direct replacement for console.info that only logs if user is admin
 * @param message The info message to log
 * @param args Additional arguments to log
 */
export const info = (message: string, ...args: any[]): void => {
  secureLogger.info(message, ...args);
};

/**
 * Direct replacement for console.debug that only logs if user is admin
 * @param message The debug message to log
 * @param args Additional arguments to log
 */
export const debug = (message: string, ...args: any[]): void => {
  secureLogger.debug(message, ...args);
}; 